import { Component, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { UserMasterService } from '../../services/user-master-service';
import { ConfigMasterService } from '../../services/config-master-service';
import { UtilityService } from 'src/app/modules/accounts-module/accounts/services/utility-service';
import { TemplateMasterService } from '../../services/template-master-service';
import { Router } from '@angular/router';
import { MatTableDataSource } from '@angular/material/table';
import { Template } from '../../models/template-model';
import { CustomerModel } from '../../models/customer-model';
import { CustomerMasterService } from '../../services/customer-master-service';
import { ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { CustomerCreateModel } from '../../models/customer-create-model';

@Component({
  selector: 'app-customer-onboarding',
  templateUrl: './customer-onboarding.component.html',
  styleUrls: ['./customer-onboarding.component.css'],
})
export class CustomerOnboardingComponent implements OnInit {
  /** Form */
  public l_formDataCustomer: CustomerModel = new CustomerModel();
  public isEditMode: boolean = false;

  /** Table */
  public dataSource = new MatTableDataSource<CustomerModel>();
  public l_customerFilteredList: CustomerModel[] = [];
  private l_clientId!: number;

  public displayedColumns: string[] = [
    'srNo',
    'customerName',
    'smsCustomerName',
    'primaryMobileNo',
    'city',
    'pincode',
    'emailId',
    'customerGstNo',
    'isActive',
    'actions',
  ];

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
  }

  constructor(
    private l_CustomerMasterService: CustomerMasterService,
    private l_UtilityService: UtilityService,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Defaults
    this.l_formDataCustomer.isActive = 'YES';
    const data = JSON.parse(sessionStorage.getItem('client_master'));
    this.l_clientId = data.clientId;

    //const state = this.location.getState() as any;

    // if (state && state.mode === 'edit' && state.data?.customerId) {
    //   this.isEditMode = true;
    //   this.l_formDataCustomer = state.data;
    // } else {
    //   this.isEditMode = false;
    // }

    this.lFN_GetCustomerList();
  }

  /** Get Customer List */
  lFN_GetCustomerList(): void {
    this.l_UtilityService.lFN_ShowLoader();

    this.l_CustomerMasterService.lFN_GetCustomerList().subscribe({
      next: (res: any) => {
        // Parse text response
        const parsed = JSON.parse(res.body);

        if (!parsed?.data || !Array.isArray(parsed.data)) {
          console.error('Invalid customer response format', parsed);
          this.dataSource.data = [];
          return;
        }

        const customers: CustomerModel[] = parsed.data.map((c: any) => ({
          customerId: c.customerId,
          customerName: c.customerName,
          smsCustomerName: c.smsCustomerName,
          primaryMobileNo: c.customerMobnoPri,
          secondaryMobileNo: c.customerMobnoSec,
          address: c.customerAddress,
          city: c.customerCity,
          pincode: c.customerPinCode,
          emailId: c.customerEmailId,
          customerGstNo: c.customerGstNo,
          isActive: c.customerIsActive,
        }));

        this.dataSource.data = customers;
      },
      error: () => {
        this.l_UtilityService.lFN_ErrorToaster(
          'Failed to load customer list',
          'Error'
        );
      },
      complete: () => {
        this.l_UtilityService.lFN_HideLoader();
      },
    });
  }

  private capitalizeWords(str: string): string {
    if (!str) return '';
    return str
      .split(/\s+/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  /** Submit (Add / Update) */
  async lFN_SubmitCustomer(form: NgForm): Promise<void> {
    if (form.invalid) {
      return;
    }

    const normalizedNewName = this.capitalizeWords(this.l_formDataCustomer.customerName.trim()).toLowerCase();
    const nameExists = this.dataSource.data.some(c => {
      const isDifferentCustomer = !this.isEditMode || c.customerId !== this.l_formDataCustomer.customerId;
      return isDifferentCustomer && (c.customerName || '').trim().toLowerCase() === normalizedNewName;
    });

    if (nameExists) {
      this.l_UtilityService.lFN_ErrorToaster('Customer with this name already exists.', 'Validation Error');
      return;
    }

    this.l_UtilityService.lFN_ShowLoader();

    try {
      /** Build API payload */
      const payload = new CustomerCreateModel({
        clientId: this.l_clientId,
        customerName: this.capitalizeWords(this.l_formDataCustomer.customerName.trim()),
        smsCustomerName: this.capitalizeWords(this.l_formDataCustomer.smsCustomerName.trim()),
        customerMobnoPri: this.l_formDataCustomer.primaryMobileNo,
        customerMobnoSec: this.l_formDataCustomer.secondaryMobileNo || null,
        customerAddress: this.l_formDataCustomer.address.trim(),
        customerCity: this.l_formDataCustomer.city.trim(),
        customerPinCode: this.l_formDataCustomer.pincode,
        customerEmailId: this.l_formDataCustomer.emailId || null,
        customerGstNo: this.l_formDataCustomer.customerGstNo || null,
        customerIsActive: this.l_formDataCustomer.isActive,
      });

      // 🔥 ONLY FOR UPDATE
      if (this.isEditMode) {
        payload.customerId = this.l_formDataCustomer.customerId;
      }

      await this.l_CustomerMasterService.lFN_SaveCustomer(payload).toPromise();

      this.l_UtilityService.lFN_SuccessToaster(
        'Customer saved successfully',
        'Success'
      );

      this.lFN_ResetForm(form);
      this.lFN_GetCustomerList(); // refresh table
    } catch (err: any) {
      console.error('Save customer error', err);

      this.l_UtilityService.lFN_ErrorToaster(
        err?.error?.message || 'Failed to save customer',
        'Error'
      );
    } finally {
      this.l_UtilityService.lFN_HideLoader();
    }
  }

  /** Edit */
  lFN_EditCustomer(c: CustomerModel): void {
    this.isEditMode = true;

    this.l_UtilityService.lFN_ShowLoader();

    this.l_CustomerMasterService.lFN_GetCustomerById(c.customerId!).subscribe({
      next: (res: any) => {
        const parsed = JSON.parse(res.body);

        if (!parsed?.data) {
          this.l_UtilityService.lFN_ErrorToaster(
            'Invalid customer data',
            'Error'
          );
          return;
        }

        const data = parsed.data;

        /** Populate form */
        this.l_formDataCustomer = {
          customerId: data.customerId,
          customerName: data.customerName,
          smsCustomerName: data.smsCustomerName,
          primaryMobileNo: data.customerMobnoPri,
          secondaryMobileNo: data.customerMobnoSec,
          address: data.customerAddress,
          city: data.customerCity,
          pincode: data.customerPinCode,
          emailId: data.customerEmailId,
          customerGstNo: data.customerGstNo,
          isActive: data.customerIsActive,
        };
        window.scrollTo({ top: 0, behavior: 'smooth' });
        this.isEditMode = true;
      },
      error: () => {
        this.l_UtilityService.lFN_ErrorToaster(
          'Failed to load customer details',
          'Error'
        );
      },
      complete: () => {
        this.l_UtilityService.lFN_HideLoader();
      },
    });
  }

  /** Status Change */
  lFN_ChangeCustomerStatus(customer: CustomerModel): void {

  const newStatus: 'YES' | 'NO' =
  customer.isActive === 'YES' ? 'NO' : 'YES';

  const statusLabel = newStatus === 'YES' ? 'Active' : 'Inactive';

  const dialogData = {
    title: `Change Customer Status - ${customer.customerName}`,
    message: `Are you sure you want to change status of ${customer.customerName} to ${statusLabel}?`,
    buttons: [
      {
        label: 'Yes',
        value: true,
        color: 'primary',
        className: 'btn-yes-custom',
      },
      { label: 'No', value: false, color: 'warn' },
    ],
  };

  this.l_UtilityService
    .lFN_ShowConfirmationDialog(
      dialogData.title,
      dialogData.message,
      dialogData.buttons
    )
    .subscribe((confirmed) => {
      if (!confirmed) return;

      // ✅ SAME CREATE MODEL USED FOR UPDATE
      const payload = new CustomerCreateModel({
        clientId: 1, // or from session
        customerId: customer.customerId,
        customerName: customer.customerName,
        customerMobnoPri: customer.primaryMobileNo,
        customerMobnoSec: customer.secondaryMobileNo,
        customerAddress: customer.address,
        customerCity: customer.city,
        customerPinCode: customer.pincode,
        customerEmailId: customer.emailId,
        customerGstNo: customer.customerGstNo,
        customerIsActive: newStatus
      });

      this.l_CustomerMasterService.lFN_SaveCustomer(payload).subscribe({
        next: (response: any) => {
          // 🔥 Optimistic UI update
          customer.isActive = newStatus;

          this.l_UtilityService.lFN_SuccessToaster(
            response?.body?.msg || 'Customer status updated successfully',
            'Success'
          );
        },
        error: (err) => {
          const errorMsg =
            err.error?.msg || 'Failed to update customer status';

          this.l_UtilityService.lFN_ErrorToaster(errorMsg, 'Error');
        },
      });
    });
}


  /** Reset */
  lFN_ResetForm(form: NgForm): void {
    form.resetForm();
    this.l_formDataCustomer = new CustomerModel();
    this.l_formDataCustomer.isActive = 'YES';
    this.isEditMode = false;
  }
}
